import { Box, Column, Heading, Row } from '@stenajs-webui/core';
import { Banner, Icon, stenaSwitch } from '@stenajs-webui/elements';
import { ModalBody, ModalHeading } from '@stenajs-webui/modal';
import { type FC, useCallback, useState } from 'react';
import type { BasicBooking } from '../../../../common/graphql/fragments/gql/BasicBooking';
import { trackEvent } from '../../../../common/tracking/trackerService';
import { resolveInformationMessageBannerVariant } from '../../../../common-ui/information-message/informationMessageUtils';
import { ModalHeaderContainer } from '../../../../common-ui/modal/ModalHeaderContainer';
import { SailingInfoHorizontal } from '../../../../common-ui/sailing-info/SailingInfoHorizontal';
import { useSwitch } from '../hooks/useSwitch';
import { switchModalCandidateColumns } from './columns';
import { SwitchInformationBanner, SwitchLoadingPreferenceBanner } from './SwitchInformationBanner';
import { SwitchModalFooter } from './SwitchModalFooter';
import { SwitchModalTable } from './SwitchModalTable';

interface Props {
  booking: BasicBooking;
  onRequestClose: () => void;
  switchCandidates: BasicBooking[];
}

export const SwitchModalContent: FC<Props> = ({ booking, switchCandidates, onRequestClose }) => {
  const { switchBookings, statusInformationList, loading, setStatusInformationList } = useSwitch();

  const [bookingToSwitch, onChangeBookingToSwitch] = useState<BasicBooking | null>(
    switchCandidates.length === 1 ? switchCandidates[0] : null,
  );

  const onChangeCandidate = (booking: BasicBooking | null) => {
    setStatusInformationList([]);
    onChangeBookingToSwitch(booking);
  };

  const onSubmit = useCallback(async () => {
    try {
      const switchBookingsResult = await switchBookings(booking, bookingToSwitch);
      if (switchBookingsResult?.success) {
        onRequestClose();
      }
    } catch (e) {
      trackEvent({ category: 'Switch', action: 'Error' });
      console.error(e);
    }
  }, [booking, bookingToSwitch, onRequestClose, switchBookings]);

  // TODO: Review
  return (
    <>
      <ModalBody>
        <ModalHeaderContainer onRequestClose={onRequestClose}>
          <Row gap={2}>
            <Icon icon={stenaSwitch} size={20} />
            <ModalHeading children={`Switch booking ${booking.bookingNo}`} />
          </Row>
        </ModalHeaderContainer>
        <SailingInfoHorizontal
          hasArrived={booking.hasArrived}
          releasedAt={booking.releasedAt}
          sailing={booking.sailing}
          bookingStatus={booking.bookingStatus.status}
        />
        <Heading variant={'h5'} as={'h2'}>
          First booking
        </Heading>
        <Box width={'100%'} overflowX={'auto'}>
          <SwitchModalTable columns={switchModalCandidateColumns} bookings={[booking]} />
        </Box>
        {switchCandidates.length === 0 && (
          <SwitchInformationBanner
            text={
              'Could not find two bookings possible to switch. You can currently only switch a Waitlist booking with a similar Confirmed booking on the same sailing.'
            }
          />
        )}
        {switchCandidates.length > 0 && (
          <>
            <Heading variant={'h5'} as={'h2'}>
              Select second booking
            </Heading>
            <Box width={'100%'} overflowX={'auto'}>
              <SwitchModalTable
                columns={switchModalCandidateColumns}
                selectedBookingNo={bookingToSwitch?.bookingNo}
                bookings={switchCandidates}
                onChange={onChangeCandidate}
              />
            </Box>
          </>
        )}
      </ModalBody>
      <SwitchModalFooter loading={loading} onClose={onRequestClose} onSubmit={onSubmit}>
        {statusInformationList.length > 0 ? (
          <Column gap>
            {statusInformationList.map((statusInformation, index) => (
              <Banner
                key={index}
                variant={resolveInformationMessageBannerVariant(statusInformation.type)}
                text={statusInformation.message}
              />
            ))}
          </Column>
        ) : (
          <SwitchLoadingPreferenceBanner booking={booking} bookingToSwitch={bookingToSwitch} />
        )}
      </SwitchModalFooter>
    </>
  );
};
